import React from "react";
import {
    Box,
    Button,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from "../ui";
import AddIcon from "../ui/icons/Add";
import DeleteIcon from "../ui/icons/Delete";
import SearchIcon from "../ui/icons/Search";

const FILTER_HINTS = {
    billNo: "Enter bill number",
    type: "Example: table, home, take or quick",
    billAddBy: "Enter sale person name",
    billStatusId: "Example: paid, partial, not paid or rejected",
    finalPrice: "Enter exact final price",
    cust: "Enter customer name or mobile",
    createdAt: "Example: 4 Oct 2026, Oct or 2026",
};

const FILTER_VALUE_OPTIONS = {
    type: ["Quick Billing", "In Table", "Home Delivery", "Take Away"],
    billStatusId: ["Initiated", "Not Paid", "Partial Paid", "Paid", "Rejected"],
};

export default function ShowBillsFilterPanel({
    fields = [],
    filters = [],
    billNumberPrefix = "Bill-",
    loading = false,
    onChange = () => { },
    onApply = () => { },
    onClear = () => { },
}) {
    const usedKeys = new Set(filters.map(filter => filter.key));
    const completedFilterCount = filters.filter(filter => String(filter.value || "").trim()).length;

    const updateFilter = (index, patch) => {
        onChange(filters.map((filter, filterIndex) => filterIndex === index
            ? { ...filter, ...patch }
            : filter));
    };

    const addFilter = () => {
        const nextField = fields.find(field => !usedKeys.has(field.key));
        if (nextField)
            onChange([...filters, { key: nextField.key, value: "" }]);
    };

    const removeFilter = (index) => {
        if (filters.length === 1) {
            onChange([{ key: fields[0]?.key || "billNo", value: "" }]);
            return;
        }
        onChange(filters.filter((filter, filterIndex) => filterIndex !== index));
    };

    const handleValueChange = (index, filter, rawValue) => {
        const value = filter.key === "billNo" ? rawValue.replace(/\D/g, "") : rawValue;
        updateFilter(index, { value });
    };

    return (
        <Paper
            elevation={2}
            sx={{
                p: { xs: 1.5, sm: 2 },
                border: "1px solid",
                borderColor: "primary.paperborder",
                borderRadius: 2,
                backgroundColor: "background.paper",
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                <Box>
                    <Typography variant="h6" color="primary">Filter Bills</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Add multiple conditions. All filled conditions are applied together.
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {completedFilterCount} condition{completedFilterCount === 1 ? "" : "s"} ready
                </Typography>
            </Box>

            <Grid container spacing={1.5}>
                {filters.map((filter, index) => {
                    const field = fields.find(item => item.key === filter.key) || fields[0];
                    const isBillNumber = filter.key === "billNo";
                    const valueOptions = FILTER_VALUE_OPTIONS[filter.key];
                    return (
                        <Grid item xs={12} key={filter.key}>
                            <Paper
                                variant="outlined"
                                elevation={0}
                                sx={{ p: 1.25, borderRadius: 1.5, backgroundColor: "rgba(0, 51, 62, 0.025)" }}
                            >
                                <Grid container spacing={1.25} alignItems="center">
                                    <Grid item xs={12} sm={4} md={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={`Filter ${index + 1}`}
                                            value={filter.key}
                                            onChange={(event) => updateFilter(index, { key: event.target.value, value: "" })}
                                        >
                                            {fields.map(option => (
                                                <MenuItem
                                                    key={option.key}
                                                    value={option.key}
                                                    disabled={option.key !== filter.key && usedKeys.has(option.key)}
                                                >
                                                    {option.value}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={10} sm={7} md={8}>
                                        <TextField
                                            select={Boolean(valueOptions)}
                                            fullWidth
                                            size="small"
                                            label={field?.value || "Value"}
                                            placeholder={FILTER_HINTS[filter.key] || "Enter filter value"}
                                            type={filter.key === "finalPrice" ? "number" : "text"}
                                            value={filter.value}
                                            onChange={(event) => handleValueChange(index, filter, event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    event.preventDefault();
                                                    onApply();
                                                }
                                            }}
                                            InputProps={isBillNumber ? {
                                                startAdornment: <InputAdornment position="start">{billNumberPrefix}</InputAdornment>,
                                            } : undefined}
                                            inputProps={isBillNumber ? {
                                                inputMode: "numeric",
                                                pattern: "[0-9]*",
                                            } : undefined}
                                        >
                                            {(valueOptions || []).map(option => (
                                                <MenuItem key={option} value={option}>{option}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={2} sm={1} md={1} sx={{ display: "flex", justifyContent: "center" }}>
                                        <IconButton
                                            aria-label={`Remove ${field?.value || "filter"}`}
                                            title="Remove filter"
                                            color="error"
                                            onClick={() => removeFilter(index)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    disabled={filters.length >= fields.length || loading}
                    onClick={addFilter}
                >
                    Add Filter
                </Button>
                <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    disabled={completedFilterCount === 0 || loading}
                    onClick={onApply}
                >
                    Search Bills
                </Button>
                <Button
                    variant="text"
                    disabled={completedFilterCount === 0 && filters.length === 1 && filters[0]?.key === fields[0]?.key}
                    onClick={onClear}
                >
                    Clear All
                </Button>
            </Box>
        </Paper>
    );
}
